import { DashboardSkeleton, Skeleton } from "components/new-stuff/ui/Skeleton"

/**
 * What fills the screen on a cold load, and while an account switch rebuilds
 * the app.
 *
 * It used to be a small spinner in the middle of a blank white page - the
 * first thing anyone saw of the product, and the least informative thing it
 * could show. The alternative people reach for is a logo animation, but that
 * is the same idea wearing a nicer coat: it still says only "wait", and it
 * still leaves the whole screen to be replaced in one jump when the app
 * arrives.
 *
 * So this draws the app instead. Sidebar, top bar, content - at the real
 * dimensions from `Layout`, so the shell the user is looking at is the shell
 * they end up with, and the load reads as the app filling in rather than the
 * app replacing a placeholder. Nothing moves at the handover except the
 * content itself.
 *
 * Purely presentational and dependency-free by design: this renders before
 * any route, store or query exists.
 */
const NAV_GROUPS = [4, 3, 5, 4] as const

/** Varied widths: menu labels are not all the same length, and even bars read as a fault. */
const NAV_ITEM_WIDTHS = ["108px", "84px", "132px", "96px", "120px"]

export const AppBootSkeleton = () => (
  <div
    className="flex h-screen w-full overflow-hidden bg-[#F3F5FB]"
    // One announcement for the whole screen, rather than one per bar.
    role="status"
    aria-label="Loading PaidHR"
  >
    {/*
      The sidebar as it really is: brand blue, the full height of the viewport, and filled to
      the bottom. Drawn in white at low opacity rather than in grey, because grey bars on blue
      read as a broken panel rather than as the sidebar arriving.
    */}
    <aside
      className="hidden h-[100dvh] w-[var(--sidenav-w)] shrink-0 flex-col gap-8 overflow-hidden px-5 py-6 md:flex"
      // Not `bg-primary500`: on a white-label domain the brand colour is injected only once
      // the branding has been fetched, and this skeleton is what covers that fetch. Without a
      // fallback the panel had no colour at all, so the sidebar vanished from the shell
      // halfway through a switch. Slate stands in until the real colour lands.
      style={{ backgroundColor: "rgb(var(--color-primary, 100 116 139))" }}
    >
      <div className="flex items-center gap-3">
        <span className="h-8 w-8 rounded-lg bg-white/25" />
        <span className="h-3 w-24 rounded-full bg-white/25" />
      </div>

      {NAV_GROUPS.map((items, group) => (
        <div
          key={group}
          className="flex flex-col gap-3"
        >
          {/* Section label, then its items - the sidebar's real rhythm. */}
          <span className="h-2 w-16 rounded-full bg-white/20" />
          {Array.from({ length: items }, (_, i) => (
            <div
              key={i}
              className="flex items-center gap-3"
            >
              <span className="h-[18px] w-[18px] rounded bg-white/25" />
              <span
                className="h-3 rounded-full bg-white/25"
                style={{ width: NAV_ITEM_WIDTHS[i % NAV_ITEM_WIDTHS.length] }}
              />
            </div>
          ))}
        </div>
      ))}

      {/* Holds the bottom of the column, so the panel reads as full rather than as one that
          ran out of content halfway down. */}
      <div className="mt-auto flex items-center gap-3">
        <span className="h-9 w-9 rounded-full bg-white/25" />
        <div className="flex flex-col gap-2">
          <span className="h-3 w-24 rounded-full bg-white/25" />
          <span className="h-2 w-16 rounded-full bg-white/20" />
        </div>
      </div>
    </aside>

    <div className="flex min-w-0 flex-1 flex-col">
      {/* Top bar - 70px, matching Layout's Header. */}
      <header className="flex h-[70px] shrink-0 items-center justify-between border-b border-[#e2e8f0] bg-white px-4 md:px-6">
        <Skeleton
          variant="block"
          width="min(320px, 45vw)"
          height="36px"
        />
        <div className="flex items-center gap-3">
          <Skeleton
            variant="circle"
            width="20px"
            height="20px"
          />
          <Skeleton
            variant="circle"
            width="32px"
            height="32px"
          />
        </div>
      </header>

      <main className="flex flex-1 flex-col gap-5 overflow-y-auto p-4 md:p-6">
        <div className="flex flex-col gap-2">
          <Skeleton
            width="180px"
            height="20px"
          />
          <Skeleton width="260px" />
        </div>

        <DashboardSkeleton />
      </main>
    </div>
  </div>
)

export default AppBootSkeleton
